import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useGetAdminUsersQuery,
  useAdminSetBlocksMutation,
  useAdminWarnUserMutation,
  useAdminRemoveWarningsMutation,
} from "../../../../store/slices/api";
import { SharedLoading, SharedFeatureError } from "../../../shared";
import { failedMessage } from "../../../shared/serverReason";

dayjs.extend(utc);
dayjs.extend(timezone);

const FEATURE_LABELS = {
  messaging: "Correspondence",
  bulletin: "Bulletin Board",
  marketplace: "Marketplace",
  connect: "Family Connect (Directory & Profiles)",
  tree: "Family Tree",
  ai: "Ask AI",
  footprint: "Blog",
  scrapbook: "The Scrapbook",
};

function ToggleSwitch({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-[8px] cursor-pointer">
      <span className="text-[14px] font-body text-dupontBlue dark:text-darkText">
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={!checked}
        onClick={() => onChange(!checked)}
        className={`relative w-[44px] h-[24px] rounded-full transition-colors duration-200 ${
          checked
            ? "bg-red-500"
            : "bg-green-500"
        }`}
      >
        <span
          className={`absolute top-[2px] left-[2px] w-[20px] h-[20px] bg-white rounded-full shadow transition-transform duration-200 ${
            checked ? "translate-x-[20px]" : ""
          }`}
        />
      </button>
    </label>
  );
}

function ManageAccessModal({ user, onClose }) {
  const [setBlocks, { isLoading: saving }] = useAdminSetBlocksMutation();
  const [blocks, setBlockState] = useState({
    messaging: user.blocked_features?.includes("messaging") || false,
    bulletin: user.blocked_features?.includes("bulletin") || false,
    marketplace: user.blocked_features?.includes("marketplace") || false,
    connect: user.blocked_features?.includes("connect") || false,
    tree: user.blocked_features?.includes("tree") || false,
    ai: user.blocked_features?.includes("ai") || false,
    footprint: user.blocked_features?.includes("footprint") || false,
    scrapbook: user.blocked_features?.includes("scrapbook") || false,
  });
  const [reason, setReason] = useState(user.feature_blocked_reason || "");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    try {
      await setBlocks({
        target_id: user.individual_id,
        blocks,
        reason: reason.trim(),
      }).unwrap();
      setSaved(true);
      setTimeout(() => onClose(), 800);
    } catch (err) {
      console.error("Failed to update blocks:", err);
      setError(failedMessage(err, "The changes didn't save. Please try again."));
    }
  };

  const anyBlocked = Object.values(blocks).some(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-darkCard rounded-[16px] shadow-xl border border-dupontGold/30 w-full max-w-[420px] mx-4">
        {/* Header */}
        <div className="px-[24px] py-[16px] border-b border-dupontGold/20">
          <h3 className="text-[18px] font-display font-bold text-dupontBlue dark:text-darkText">
            Manage Access
          </h3>
          <p className="text-[13px] text-dupontGray dark:text-darkTextSecondary mt-1">
            {user.name}
          </p>
        </div>

        {/* Toggle switches */}
        <div className="px-[24px] py-[16px]">
          <p className="text-[12px] text-dupontGray dark:text-darkTextSecondary mb-[12px] uppercase tracking-wide font-semibold">
            Feature Access (red = blocked)
          </p>
          <div className="divide-y divide-dupontGold/10">
            {Object.entries(FEATURE_LABELS).map(([key, label]) => (
              <ToggleSwitch
                key={key}
                label={label}
                checked={blocks[key]}
                onChange={(val) =>
                  setBlockState((prev) => ({ ...prev, [key]: val }))
                }
              />
            ))}
          </div>
        </div>

        {/* Reason (only shown if something is blocked) */}
        {anyBlocked && (
          <div className="px-[24px] pb-[16px]">
            <label className="text-[12px] text-dupontGray dark:text-darkTextSecondary uppercase tracking-wide font-semibold">
              Reason (visible to user)
            </label>
            <textarea
              className="w-full mt-[6px] rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 p-3 text-sm focus:ring-2 focus:ring-dupontGold focus:border-transparent"
              rows={2}
              maxLength={500}
              placeholder="Why is this user being restricted?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}

        {error && (
          <p role="alert" className="px-[24px] pb-[12px] text-[13px] font-body text-red-700 dark:text-red-400">
            {error}
          </p>
        )}

        {/* Footer */}
        <div className="px-[24px] py-[16px] border-t border-dupontGold/20 flex justify-end gap-[8px]">
          <button
            onClick={onClose}
            className="px-[16px] py-[8px] text-[13px] font-display font-semibold rounded-[8px] border border-gray-300 dark:border-gray-600 text-dupontGray dark:text-darkTextSecondary hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-[16px] py-[8px] text-[13px] font-display font-semibold rounded-[8px] bg-dupontGold text-white hover:bg-dupontGold/90 disabled:opacity-50"
          >
            {saved ? "Saved!" : saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WarnModal({ user, individualId, onClose }) {
  const [warnUser, { isLoading }] = useAdminWarnUserMutation();
  const [reason, setReason] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSend = async () => {
    if (!reason.trim()) return;
    setError(null);
    try {
      await warnUser({
        target_id: user.individual_id,
        individual_id: individualId,
        reason: reason.trim(),
      }).unwrap();
      setSent(true);
      setTimeout(() => onClose(), 800);
    } catch (err) {
      console.error("Failed to send warning:", err);
      setError(failedMessage(err, "The warning wasn't sent. Please try again."));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-darkCard rounded-[16px] shadow-xl border border-dupontGold/30 w-full max-w-[420px] mx-4">
        <div className="px-[24px] py-[16px] border-b border-dupontGold/20">
          <h3 className="text-[18px] font-display font-bold text-dupontBlue dark:text-darkText">
            Send Warning
          </h3>
          <p className="text-[13px] text-dupontGray dark:text-darkTextSecondary mt-1">
            To: {user.name}
          </p>
        </div>
        <div className="px-[24px] py-[16px]">
          <textarea
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 p-3 text-sm focus:ring-2 focus:ring-dupontGold focus:border-transparent"
            rows={3}
            maxLength={600}
            placeholder="Describe the warning reason..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {error && (
            <p role="alert" className="mt-[8px] text-[13px] font-body text-red-700 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
        <div className="px-[24px] py-[16px] border-t border-dupontGold/20 flex justify-end gap-[8px]">
          <button
            onClick={onClose}
            className="px-[16px] py-[8px] text-[13px] font-display font-semibold rounded-[8px] border border-gray-300 dark:border-gray-600 text-dupontGray dark:text-darkTextSecondary hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isLoading || !reason.trim()}
            className="px-[16px] py-[8px] text-[13px] font-display font-semibold rounded-[8px] bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {sent ? "Sent!" : isLoading ? "Sending..." : "Send Warning"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserManagement({ individualId }) {
  const { data: users, isLoading, isFetching, isError, refetch } =
    useGetAdminUsersQuery(individualId);
  const [removeWarnings] = useAdminRemoveWarningsMutation();
  const [manageUser, setManageUser] = useState(null);
  const [warnUser, setWarnUser] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Also while "Try again" runs: RTK Query then reports neither loading nor
  // error and no data, which would leave the tab blank.
  if (isLoading || (!users && isFetching)) {
    return <SharedLoading text="Loading users..." />;
  }

  // A failed load says so, with a retry.
  if (isError && !users) {
    return <SharedFeatureError title="We couldn't load the users" onRetry={refetch} />;
  }

  if (!users) {
    return null;
  }

  if (users.length === 0) {
    return (
      <div className="text-[15px] text-dupontGray dark:text-darkTextSecondary">
        No connected users yet.
      </div>
    );
  }

  const handleRemoveWarnings = async (user) => {
    if (!window.confirm(`Remove all warning labels from ${user.name}?`)) return;
    setActionError(null);
    try {
      await removeWarnings({ target_id: user.individual_id }).unwrap();
    } catch (err) {
      console.error("Failed to remove warnings:", err);
      setActionError(
        failedMessage(err, `The warnings on ${user.name} weren't cleared. Please try again.`)
      );
    }
  };

  return (
    <div className="flex flex-col gap-y-[16px]">
      <div className="text-[14px] text-dupontGray dark:text-darkTextSecondary">
        {users.length} connected user{users.length !== 1 ? "s" : ""}
      </div>
      {actionError && (
        <p role="alert" className="text-[14px] font-body text-red-700 dark:text-red-400">
          {actionError}
        </p>
      )}
      <div className="bg-white dark:bg-darkCard rounded-[16px] shadow-md border border-dupontGold/30 overflow-hidden overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-dupontCream dark:bg-darkPrimary border-b border-dupontGold/30">
              <th className="px-[16px] py-[12px] text-[13px] font-bold text-dupontBlue dark:text-darkText">
                Name
              </th>
              <th className="px-[16px] py-[12px] text-[13px] font-bold text-dupontBlue dark:text-darkText">
                Email
              </th>
              <th className="px-[16px] py-[12px] text-[13px] font-bold text-dupontBlue dark:text-darkText">
                Status
              </th>
              <th className="px-[16px] py-[12px] text-[13px] font-bold text-dupontBlue dark:text-darkText">
                Joined
              </th>
              <th className="px-[16px] py-[12px] text-[13px] font-bold text-dupontBlue dark:text-darkText">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const hasBlocks =
                user.blocked_features && user.blocked_features.length > 0;
              return (
                <tr
                  key={user.id}
                  className="border-b border-dupontGold/10 last:border-b-0"
                >
                  <td className="px-[16px] py-[10px] text-[14px] text-dupontBlue dark:text-darkText font-medium">
                    {user.name}
                  </td>
                  <td className="px-[16px] py-[10px] text-[14px] text-dupontGray dark:text-darkTextSecondary">
                    {user.email || "\u2014"}
                  </td>
                  <td className="px-[16px] py-[10px]">
                    <div className="flex flex-wrap gap-[4px]">
                      {!hasBlocks && !user.has_active_warnings && (
                        <span className="text-[11px] font-bold px-[8px] py-[2px] rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          Full Access
                        </span>
                      )}
                      {hasBlocks &&
                        user.blocked_features.map((feat) => (
                          <span
                            key={feat}
                            className="text-[11px] font-bold px-[8px] py-[2px] rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                          >
                            {FEATURE_LABELS[feat] || feat}
                          </span>
                        ))}
                      {user.has_active_warnings && (
                        <span className="text-[11px] font-bold px-[8px] py-[2px] rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                          Warned
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-[16px] py-[10px] text-[14px] text-dupontGray dark:text-darkTextSecondary">
                    {dayjs(user.created_at)
                      .utc()
                      .local()
                      .format("MMM D, YYYY")}
                  </td>
                  <td className="px-[16px] py-[10px]">
                    <div className="flex gap-[6px] flex-wrap">
                      <button
                        onClick={() => setManageUser(user)}
                        className="text-[12px] font-semibold px-[10px] py-[4px] rounded-[6px] bg-dupontBlue dark:bg-darkGold text-white hover:opacity-90 transition-opacity"
                      >
                        Manage Access
                      </button>
                      <button
                        onClick={() => setWarnUser(user)}
                        className="text-[12px] font-semibold px-[10px] py-[4px] rounded-[6px] bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                      >
                        Warn
                      </button>
                      {user.has_active_warnings && (
                        <button
                          onClick={() => handleRemoveWarnings(user)}
                          className="text-[12px] font-semibold px-[10px] py-[4px] rounded-[6px] border border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                        >
                          Clear Warnings
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {manageUser && (
        <ManageAccessModal
          user={manageUser}
          individualId={individualId}
          onClose={() => setManageUser(null)}
        />
      )}
      {warnUser && (
        <WarnModal
          user={warnUser}
          individualId={individualId}
          onClose={() => setWarnUser(null)}
        />
      )}
    </div>
  );
}

export default UserManagement;
